import { ChipGroup, FilterChip } from '@zillow/constellation-10';
import { memo, useCallback, useContext, useEffect, useState } from 'react';
import {
  SatelliteConversationReadRepliedStatusTypeV2,
  SatelliteConversationRenterProgressStatusTypeV2,
} from '@zg-rentals/ts-satellite-client';
import {
  APPLICANTS_FILTER_CLICK_EVENT_TYPE_ID,
  emitApplicantsFilterClickEvent,
  emitInquiriesFilterClickEvent,
  emitTenantsFilterClickEvent,
  emitToursFilterClickEvent,
  emitViewAllFilterClickEvent,
  INQUIRIES_FILTER_CLICK_EVENT_TYPE_ID,
  TENANTS_FILTER_CLICK_EVENT_TYPE_ID,
  TOURS_FILTER_CLICK_EVENT_TYPE_ID,
  VIEW_ALL_FILTER_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/filterTabs';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import useInboxRouterHelper from '../../hooks/useInboxRouterHelper';
import { useAuth } from '../../AuthProvider';
import { inboxPageIsLoadingStore } from '../../lib/inboxSearchListStores';

const RentalJourneyFilters = (): JSX.Element => {
  const inboxPageIsLoading = inboxPageIsLoadingStore.useValue();
  const { renterProgressStatus, folder } = useContext(InboxPageContext);
  const { changeFiltersOrFolders } = useInboxRouterHelper();
  const [currentRenterProgressStatus, setCurrentRenterProgressStatus] = useState(renterProgressStatus);
  const { user } = useAuth();

  useEffect(() => {
    setCurrentRenterProgressStatus(renterProgressStatus);
  }, [renterProgressStatus]);

  const getEventDetailsForSelectedFilter = useCallback(
    (selectedFilterTab: SatelliteConversationRenterProgressStatusTypeV2) => {
      switch (selectedFilterTab) {
        case SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL:
          return emitViewAllFilterClickEvent;
        case SatelliteConversationRenterProgressStatusTypeV2.INQUIRIES:
          return emitInquiriesFilterClickEvent;
        case SatelliteConversationRenterProgressStatusTypeV2.TOURS:
          return emitToursFilterClickEvent;
        case SatelliteConversationRenterProgressStatusTypeV2.APPLICANTS:
          return emitApplicantsFilterClickEvent;
        case SatelliteConversationRenterProgressStatusTypeV2.TENANTS:
          return emitTenantsFilterClickEvent;
        default:
          return;
      }
    },
    [],
  );

  const handleFilterClick = (selectedFilterTab: string | undefined) => {
    // ChipGroup fires onSelectionChange(undefined) when the user re-clicks the already-selected
    // chip (single-select toggle-off). Treat that as a no-op so the active filter does not
    // silently clear.
    if (!selectedFilterTab || inboxPageIsLoading || selectedFilterTab === currentRenterProgressStatus) {
      return;
    }
    const filterTab = selectedFilterTab as SatelliteConversationRenterProgressStatusTypeV2;

    const clickstreamEvent = getEventDetailsForSelectedFilter(filterTab);
    clickstreamEvent?.(user);

    setCurrentRenterProgressStatus(filterTab);
    changeFiltersOrFolders({
      renterProgressStatus: filterTab,
      folder,
      // Reset other filters
      readRepliedStatus: SatelliteConversationReadRepliedStatusTypeV2.SHOW_ALL,
    });
  };

  return (
    <ChipGroup
      aria-label="Renter progress filters"
      selected={currentRenterProgressStatus}
      onSelectionChange={(value) => handleFilterClick(typeof value === 'string' ? value : undefined)}
      css={{
        flexWrap: 'nowrap',
        minWidth: 'max-content',
      }}
    >
      <FilterChip
        data-eventid={VIEW_ALL_FILTER_CLICK_EVENT_TYPE_ID}
        value={SatelliteConversationRenterProgressStatusTypeV2.SHOW_ALL}
      >
        View All
      </FilterChip>
      <FilterChip
        data-eventid={INQUIRIES_FILTER_CLICK_EVENT_TYPE_ID}
        value={SatelliteConversationRenterProgressStatusTypeV2.INQUIRIES}
      >
        Inquiries
      </FilterChip>
      <FilterChip
        data-eventid={TOURS_FILTER_CLICK_EVENT_TYPE_ID}
        value={SatelliteConversationRenterProgressStatusTypeV2.TOURS}
      >
        Tours
      </FilterChip>
      <FilterChip
        data-eventid={APPLICANTS_FILTER_CLICK_EVENT_TYPE_ID}
        value={SatelliteConversationRenterProgressStatusTypeV2.APPLICANTS}
      >
        Applicants
      </FilterChip>
      <FilterChip
        data-eventid={TENANTS_FILTER_CLICK_EVENT_TYPE_ID}
        value={SatelliteConversationRenterProgressStatusTypeV2.TENANTS}
      >
        Tenants
      </FilterChip>
    </ChipGroup>
  );
};

export default memo(RentalJourneyFilters);
